import React, { useState, useEffect } from "react";
import { CheckCircle2, Clock, Save, RefreshCw, AlertCircle } from "lucide-react";
import { fetchCategorySettings, updateCategorySettings } from "../system/settingsApi";

export const SessionPolicyPage: React.FC = () => {
  const [session, setSession] = useState({
    idle_timeout_minutes: 30,
    max_session_duration_hours: 8,
    max_concurrent_sessions: 3,
    remember_me_days: 14,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchCategorySettings("session_policy");
      if (res.data) {
        setSession((prev) => ({ ...prev, ...res.data }));
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load session policy.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await updateCategorySettings("session_policy", session);
      setSaved(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save session policy.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400 text-xs">
        <RefreshCw className="w-5 h-5 animate-spin mr-2 text-indigo-400" />
        <span>Loading session policy...</span>
      </div>
    );
  }

  return (
    <div className="p-5 bg-slate-900/90 border border-slate-800 rounded-xl shadow-md text-xs font-sans space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-950/60 border border-red-800 text-red-300 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between pb-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-indigo-400" />
          <h2 className="text-base font-bold text-slate-100">Session Timeout & Device Concurrency Limits</h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-lg shadow-md transition-colors text-xs"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin text-white" />
              <span>Saving...</span>
            </>
          ) : saved ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-white" />
              <span>Policy Saved!</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save Policy</span>
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">
            Idle Inactivity Timeout (Minutes)
          </label>
          <input
            type="number"
            value={session.idle_timeout_minutes}
            onChange={(e) => {
              setSaved(false);
              setSession({ ...session, idle_timeout_minutes: Number(e.target.value) });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono font-bold"
          />
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">
            Max Concurrent Logged-In Devices per User
          </label>
          <input
            type="number"
            value={session.max_concurrent_sessions}
            onChange={(e) => {
              setSaved(false);
              setSession({ ...session, max_concurrent_sessions: Number(e.target.value) });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono font-bold"
          />
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">
            Maximum Session Lifespan (Hours)
          </label>
          <input
            type="number"
            value={session.max_session_duration_hours}
            onChange={(e) => {
              setSaved(false);
              setSession({ ...session, max_session_duration_hours: Number(e.target.value) });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono font-bold"
          />
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">
            Remember Me Token Validity (Days)
          </label>
          <input
            type="number"
            value={session.remember_me_days}
            onChange={(e) => {
              setSaved(false);
              setSession({ ...session, remember_me_days: Number(e.target.value) });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono font-bold"
          />
        </div>
      </div>
    </div>
  );
};
